import store from 'main/store'
import dashboardApi from 'services/api/dashboard'
import LocalUserService from 'services/localUserService'
import { getSavedSubsidiary } from 'utils/helpers'

const userService = new LocalUserService()

function normalizeDashboardUserId(value) {
  if (value == null || value === '') {
    return null
  }
  const n = typeof value === 'number' ? value : Number.parseInt(String(value), 10)
  if (!Number.isFinite(n) || n <= 0) {
    return null
  }
  return n
}

/**
 * Ensures LocalUserService has a positive dashboardUserId for Electron flows by calling
 * login/authorize when the id is missing but session and subsidiary context exist.
 *
 * @param {{ subsidiaryId?: string }} [options]
 * @returns {Promise<{ dashboardUserId: number|null, sessionId?: string, userToken?: string, userId?: string }>}
 */
export async function ensureDashboardUserIdForElectronSession(options = {}) {
  const { subsidiaryId: subsidiaryIdOption } = options
  const info = userService.getCurrentUserInfo()
  const existing = normalizeDashboardUserId(info?.dashboardUserId)
  if (existing != null) {
    // Still return session/userToken from LocalUserService so Electron main can sync
    // DASHBOARD_WEB_USER_TOKEN when authorize renew is skipped but web token exists here.
    return {
      dashboardUserId: existing,
      sessionId: info?.sessionId,
      userToken: info?.userToken,
      userId: info?.userId
    }
  }

  if (!window?.electron?.postMessage) {
    return {
      dashboardUserId: null,
      sessionId: undefined,
      userToken: undefined,
      userId: undefined
    }
  }

  if (!info?.sessionId || !info?.userToken) {
    return {
      dashboardUserId: null,
      sessionId: undefined,
      userToken: undefined,
      userId: undefined
    }
  }

  const saved = getSavedSubsidiary()
  const subsidiaryId =
    subsidiaryIdOption ||
    store.getState().login?.currentHotelId ||
    (saved && saved.subsidiaryId)

  if (!subsidiaryId) {
    return {
      dashboardUserId: null,
      sessionId: undefined,
      userToken: undefined,
      userId: undefined
    }
  }

  const lastGeoLocation = store.getState().signIn?.geoLocation || {
    latitude: 0,
    longitude: 0
  }

  try {
    const sessionRenewResponse = await dashboardApi.renewSession({
      sessionId: info.sessionId,
      userToken: info.userToken,
      geoLocation: lastGeoLocation,
      screen: `${window.screen.width}x${window.screen.height}`,
      subsidiaryId
    })

    if (!sessionRenewResponse?.isSuccess || !sessionRenewResponse.result) {
      return {
        dashboardUserId: null,
        sessionId: undefined,
        userToken: undefined,
        userId: undefined
      }
    }

    const result = sessionRenewResponse.result
    userService.updateSessionInfo(result.sessionId, result.userToken, {
      userId: result.userId,
      dashboardUserId: result.dashboardUserId
    })

    const resolved = normalizeDashboardUserId(result.dashboardUserId)
    return {
      dashboardUserId: resolved,
      sessionId: result.sessionId,
      userToken: result.userToken,
      userId: result.userId
    }
  } catch (e) {
    console.warn('ensureDashboardUserIdForElectronSession: renewSession failed', e)
    return {
      dashboardUserId: null,
      sessionId: undefined,
      userToken: undefined,
      userId: undefined
    }
  }
}
